import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { isPersistentEditCandidateEditable } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import {
  useDisambiguateAiScriptCellOverride,
  useGetAiScriptOverrideCandidates,
} from "@/payroll/ai-script/query";
import type {
  IAiSpreadsheetCellLocation,
  IAiSpreadsheetCellOverride,
  IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import { RowPickerCandidate } from "./row-picker-candidate";
import {
  LoadError,
  PersistentEditValue,
  SectionLabel,
  showPersistentEditMutationError,
} from "./shared";

interface RowPickerProps {
  override: IAiSpreadsheetCellOverride;
  businessId: string | null;
  runId: string;
  sheets: Pick<IAiSpreadsheetSheet, "id" | "editable">[];
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  onResolved: () => void;
  onWorkbookMutated?: () => void;
}

export const RowPicker = ({
  override,
  businessId,
  runId,
  sheets,
  onGoToCell,
  onResolved,
  onWorkbookMutated,
}: RowPickerProps) => {
  const { data, isLoading, isError, refetch } =
    useGetAiScriptOverrideCandidates(businessId, runId, override.id);
  const disambiguate = useDisambiguateAiScriptCellOverride(businessId);
  const candidates = data?.candidates ?? [];

  return (
    <Stack className="w-full" align="start" gap={2}>
      <SectionLabel>Which row is this edit for?</SectionLabel>
      <Text variant="caption" tone="muted">
        Your edit (
        <PersistentEditValue value={override.value?.computed_value} />) matched
        more than one row. Pick the one it belongs to.
      </Text>
      {isLoading ? (
        <Text variant="caption" tone="muted">
          Loading rows…
        </Text>
      ) : isError ? (
        <LoadError
          message="Couldn't load the matching rows."
          onRetry={() => void refetch()}
        />
      ) : candidates.length === 0 ? (
        <Text variant="caption" tone="muted">
          None of the matching rows are on this run.
        </Text>
      ) : (
        <Stack className="w-full" align="start" gap={2}>
          {candidates.map((candidate) => {
            const isEditable = isPersistentEditCandidateEditable(
              candidate.sheet_id,
              sheets,
            );
            const isSelectedCandidate =
              disambiguate.isPending &&
              disambiguate.variables?.cellId === candidate.cell_id;

            return (
              <RowPickerCandidate
                key={candidate.cell_id}
                candidate={candidate}
                isEditable={isEditable}
                isSelected={isSelectedCandidate}
                isPending={disambiguate.isPending}
                onView={() => onGoToCell(candidate)}
                onSelect={() =>
                  disambiguate.mutate(
                    {
                      runId,
                      overrideId: override.id,
                      cellId: candidate.cell_id,
                    },
                    {
                      onSuccess: () => {
                        onWorkbookMutated?.();
                        onResolved();
                      },
                      onError: (error) => {
                        if (
                          showPersistentEditMutationError(
                            "Couldn't apply the edit to that row",
                            error,
                          )
                        ) {
                          onResolved();
                        }
                      },
                    },
                  )
                }
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
